/**
 * "updating..." — the canvas message strip's note for an operation the user is
 * waiting through (requested 2026-10-22, Standard and Hybrid alike).
 *
 * Every other note on that strip is raised by the one flow that owns it
 * ("adding section...", "deleting section...", "duplicating accessory...") —
 * see `boardActions.noteAddingSection`. An ordinary save has no such owner:
 * there are ~140 write call sites across the board, in RTK Query endpoints and
 * in hand-written `fetch()`es (commitAirConditions, the psychrometric popup,
 * the selector `*Api.ts` modules), and the ask is for the note on ALL of them.
 *
 * So this module tracks board activity from two sources and raises the note
 * while either is live:
 *
 *  1. **Every write request**, counted at `window.fetch` — the one place they
 *     genuinely share. RTK Query's `fetchBaseQuery` calls the global too, so
 *     wrapping it once catches both halves with no edit to any call site, and
 *     no write added later can forget to raise the note.
 *  2. **Explicit holds** (`beginBoardActivity`), for the waits that are not one
 *     request: the airflow-direction flip (a PATCH, then a lug re-read, then
 *     the canvas settling) and the board's own post-write churn — the refetch
 *     and redraw AhuBoardPage goes through after a write, which is GET-driven
 *     and so invisible to the counter.
 *
 * Counted, not a plain toggle, for the same reason the insert note is: a field
 * save, a canvas drag and a selector write can easily overlap, and the first
 * reply must not take the note down while the others are still out.
 *
 * ★ GETs are deliberately NOT counted. The board re-reads the tunnel list
 * every few seconds and the header polls notifications, so counting reads
 * would pin the note up for good. The GET waits that ARE the user's operation
 * come in through the holds above instead.
 *
 * ★ Cross-origin requests are not counted either — PostHog ingestion (every
 * tracked event is a POST to `__KSDM_CONFIG__.posthog.host`) is the one that
 * matters, and the user is not waiting for it.
 *
 * Driven with `classList` rather than React state, like the rest of the strip:
 * `ahuBoardOverrides.scss` yields `.board-loading-note` to any active note with
 * `:has()`, which reads the class the moment it is set — with a render in
 * between there would be a tick painting both.
 */

type FetchFn = typeof window.fetch;

/** Write requests out right now. */
let inFlight = 0;
/** Open `beginBoardActivity` holds. */
let holds = 0;
/** Number of live installs — symmetric so a double mount cannot double-wrap. */
let installs = 0;
let wrapper: FetchFn | null = null;
let native: FetchFn | null = null;

const paintNote = (): void => {
  document
    .querySelector('.updating-note')
    ?.classList[inFlight + holds > 0 ? 'add' : 'remove']('active');
};

const settle = (delta: number): void => {
  inFlight = Math.max(0, inFlight + delta);
  paintNote();
};

/** The method a `fetch()` call will actually send (a `Request` carries its own). */
const methodOf = (input: RequestInfo | URL, init?: RequestInit): string => {
  const method = init?.method ?? (input instanceof Request ? input.method : undefined);
  return (method ?? 'GET').toUpperCase();
};

const urlOf = (input: RequestInfo | URL): string =>
  typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);

/** A write this app is making — same-origin, and not a read. */
const isTrackedWrite = (input: RequestInfo | URL, init?: RequestInit): boolean => {
  const method = methodOf(input, init);
  if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') return false;
  try {
    return new URL(urlOf(input), window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
};

/**
 * Hold the note up for a wait that is not a single request — a flow that spans
 * a request plus the re-read and redraw after it, or the board's own
 * post-write churn. Returns the release, which is safe to call twice.
 */
export const beginBoardActivity = (): (() => void) => {
  holds += 1;
  paintNote();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds = Math.max(0, holds - 1);
    paintNote();
  };
};

/**
 * Start counting write requests and painting `.updating-note`. Returns the
 * uninstaller.
 *
 * The global is restored only if it is still ours: if something wrapped
 * `window.fetch` on top of this one, putting the original back would drop that
 * wrapper. Ours then stays, harmlessly — with the note's markup gone,
 * `paintNote` is a no-op.
 */
export const installBoardActivityNote = (): (() => void) => {
  installs += 1;
  if (installs === 1) {
    const original = window.fetch.bind(window);
    native = original;
    wrapper = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const response = original(input, init);
      if (!isTrackedWrite(input, init)) return response;
      settle(1);
      // Both outcomes clear it: "until the request completes or fails".
      return response.finally(() => settle(-1));
    };
    window.fetch = wrapper;
  }
  return () => {
    installs = Math.max(0, installs - 1);
    if (installs > 0) return;
    if (wrapper && window.fetch === wrapper && native) window.fetch = native;
    wrapper = null;
    native = null;
    // A request still out keeps a reference to the wrapper and will decrement
    // a counter nobody is painting any more; `settle`'s clamp absorbs it.
    inFlight = 0;
    paintNote();
  };
};
